import { useEffect, useState } from "react";
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useDashboardUI } from "../context/GlobalContext";
import { Eye, EyeOff, Pencil } from "lucide-react";
import { checkSSOLogin } from "../services/authService";

export default function LoginForm() {

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pwVisible, setPwVisible] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const { setPageLoader, facility_name } = useDashboardUI();
  const [errors, setErrors] = useState({ email: "" });



  const [emailCheckingSSO, setEmailCheckingSSO] = useState(false);

  const normalizeEmail = (value) => value?.trim().toLowerCase();

  const handleEmailChange = () => {
    setShowPassword(false);
  };


  // Load Remember Me state
  useEffect(() => {
    const isRememberMeChecked = localStorage.getItem("rememberMe");
    const storedEmail = localStorage.getItem("email") || "";
    if (isRememberMeChecked) {
      setIsChecked(true);
      setEmail(storedEmail);
    }
  }, []);

  // Handle checkbox change
  const onChangeCheckbox = (event) => {
    const checked = event.target.checked;
    if (checked) {
      localStorage.setItem("email", email);
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("email");
      localStorage.removeItem("rememberMe");
    }
    setIsChecked(checked);
  };

  // Handle email input change
  const handleSetEmail = (event) => {
    const newEmail = event.target.value;
    if (localStorage.getItem("rememberMe")) {
      localStorage.setItem("email", newEmail);
    }
    setEmail(newEmail);
    setIsEmailValid(event.target.checkValidity());
  };

  // Form submit
  const handleSubmit = async (event, isGoogleLogin = false, token = "") => {
    event.preventDefault();
    setLoading(true);
    setPageLoader(true);
    setError(null);

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!apiBaseUrl) {
      setError("Configuration error. Please contact support.");
      setLoading(false);
      setPageLoader(false);
      return;
    }

    try {
      let payload = {};

      if (!password) {
        toast.error("Please enter password");
        setLoading(false);
        setPageLoader(false);
        return;
      }
      payload = {
        email: email?.toLowerCase(),
        password: password?.toString(),
      };

      await getRedirectUri(token);
      return;
    } catch (err) {
      console.error("Login error:", err);
      toast.error(err?.message || "Something went wrong. Please try again.");
      setLoading(false);
      setPageLoader(false);
    }
  };

  // Check SSO before showing password field
  const checkSSO = async () => {
    try {
      setLoading(true);
      setPageLoader(true);
      const userEmail = email?.toString().trim();
      const response = await checkSSOLogin(userEmail);
      setLoading(false);
      setPageLoader(false);

      if (response?.sso_enabled) {
        const ssoURL = `${process.env.REACT_APP_COGNITO_BASE_URL}?client_id=${process.env.REACT_APP_COGNITO_APP_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_COGNITO_REDIRECT_URI}&scope=email+openid&response_type=code&identity_provider=${response?.idpIdentifier}`;
        window.location.href = ssoURL;
      } else {
        setShowPassword(true);
      }
    }
    catch (e) {
      const message = e.response?.data?.detail || e?.message || "Something went wrong";
      toast.error(message);
      setLoading(false);
      setPageLoader(false);
    }
  };

  // Handle redirect after login
  const getRedirectUri = async (googleToken) => {
    try {
      setPageLoader(true);
      const searchParams = new URLSearchParams(window.location.search);

      const formFields = {
        grant_type: searchParams.get("grant_type") || "",
        client_id: searchParams.get("client_id") || "",
        email: email?.toString() || "",
        password: password?.toString() || "",
        redirect_uri: searchParams.get("redirect_uri") || "",
        response_type: searchParams.get("response_type") || "",
        state: searchParams.get("state") || "",
        google_token: googleToken || "",
      };

      const formData = new URLSearchParams({
        client_id: searchParams.get("client_id") || "",
        email: email?.toString(),
        password: password?.toString(),
        redirect_uri: searchParams.get("redirect_uri") || "",
        response_type: searchParams.get("response_type") || ""
      });

      try {
        const validateUserData = await fetch(`${process.env.REACT_APP_API_BASE_URL}oauth/validate/user`, {
          method: "POST",
          body: formData
        });
        if (validateUserData.status != 200) {
          let errorData = {};
          try {
            errorData = await validateUserData.json();
          } catch {
            errorData = { detail: "Something went wrong." };
          }
          setLoading(false);
          setPageLoader(false);
          toast.error(errorData?.detail || "Login failed. Please try again.");
          return;
        }
      } catch (error) {
        setLoading(false);
        setPageLoader(false);
        toast.error(error?.detail || "Login failed. Please try again.");
        return;
      }

      //If validation success → then submit form
      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${process.env.REACT_APP_API_BASE_URL}oauth/authorize`;

      // send url params as body fields
      for (const [key, value] of searchParams.entries()) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      }

      // existing form fields
      for (const [key, value] of Object.entries(formFields)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();


    } catch (err) {
      setLoading(false);
      setPageLoader(false);
      console.error("getRedirectUri Error:", err);
      toast.error("Something went wrong. Please try again.");
    }
  };


  return (
    <div className="w-full flex items-center justify-center h-full py-6 px-5 pt-[25%] pb-[36%]">
      <div className="w-full max-w-md py-0 px-5">
        <div className="flex flex-col ml-[-18px]">
          <img
            src="https://genserve.blob.core.windows.net/general-files/GenServeAi_loght_theme.png"
            alt="GenServe AI Logo"
            width={220}
          />
        </div>

        <h2 className="text-[28px] font-semibold text-gray-800">
         Sign in to your account
        </h2>

        <div className="mt-4 mb-4 relative">
          <label
            htmlFor="email"
            className="text-sm text-gray-700 mb-1 block"
          >
            Email
          </label>

          <input
            type="email"
            name="email"
            id="email"
            placeholder="name@company.com"
            value={email}
            onChange={handleSetEmail}
            onBlur={() => {
              const normalized = normalizeEmail(email);
              if (!normalized) {
                setErrors((e) => ({ ...e, email: "Email is required." }));
              } else if (!isEmailValid) {
                setErrors((e) => ({ ...e, email: "Enter a valid email." }));
              } else {
                setErrors((e) => ({ ...e, email: "" }));
              }
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !showPassword &&
                isEmailValid &&
                !emailCheckingSSO &&
                !loading
              ) {
                e.preventDefault();
                checkSSO();
              }
            }}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "err-email" : undefined}
            autoComplete="username"
            inputMode="email"
            disabled={emailCheckingSSO || loading || showPassword}
            className={`w-full border ${errors.email ? "border-red-500" : "border-gray-300"
              } rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--facility-primary))]`}
          />

          {/* Pencil icon */}
          {showPassword && (
            <button
              type="button"
              className="absolute right-3 top-[70%] -translate-y-1/2 text-gray-500 hover:text-gray-700"
              onClick={handleEmailChange}
            >
              <Pencil size={16} />
            </button>
          )}

          {errors.email && (
            <p id="err-email" className="text-xs text-red-600 mt-1">
              {errors.email}
            </p>
          )}
        </div>

        {/* Password field */}
        {showPassword && <div className="mb-4 relative">
          <label className="block font-sora font-normal text-sm font-medium text-neutral-10 lineHeight-18 mb-1">
            Password
          </label>
          <input
            type={pwVisible ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-[14px] py-[10px] border border-neutral-5 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] bg-white"
          />
          <button
            type="button"
            aria-label={pwVisible ? "Hide password" : "Show password"}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setPwVisible((v) => !v)}
            className="absolute right-2 top-1/2 p-1 rounded"
          >
            {pwVisible ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>}

        {/* Submit Button */}
        {!showPassword && (
          <button
            onClick={checkSSO}
            disabled={loading || !isEmailValid}
            className={`w-full bg-blue-500 text-white py-2.5 px-2 rounded-lg font-semibold transition duration-200 
      ${loading || !isEmailValid
                ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                : "bg-blue-500 hover:bg-[hsl(var(--primary-hover))] text-white"
              }`}
          >
            Continue
          </button>
        )}
        {showPassword && (
          <div className="flex flex-col gap-3">
            {/* ✅ Sign In Button */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-blue-500 text-white py-2.5 px-2 rounded-lg font-semibold transition duration-200"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>

          </div>
        )}

      </div>
    </div>
  );
};